import { motion } from 'framer-motion';
import { Clock, BarChart3 } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { HourlyPattern } from '@/hooks/useQRAnalytics';
import { cn } from '@/lib/utils';

interface TimePatternsProps {
  hourlyPatterns: HourlyPattern[];
  bestTimes: {
    byDay: { name: string; count: number }[];
    byHour: { name: string; hour: number; count: number }[];
  };
}

const HOURS = ['12a', '1a', '2a', '3a', '4a', '5a', '6a', '7a', '8a', '9a', '10a', '11a', '12p', '1p', '2p', '3p', '4p', '5p', '6p', '7p', '8p', '9p', '10p', '11p'];
const DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

const CustomTooltip = ({ active, payload }: any) => {
  if (active && payload && payload.length) {
    return (
      <div className="bg-popover border border-border rounded-lg shadow-lg p-3">
        <p className="text-sm font-medium">{payload[0].payload.name}</p>
        <p className="text-sm text-primary">{payload[0].value} scans</p>
      </div>
    );
  }
  return null;
};

export function TimePatterns({ hourlyPatterns, bestTimes }: TimePatternsProps) {
  const maxCount = Math.max(...hourlyPatterns.map(p => p.count), 1);
  
  // Create heatmap grid
  const getHeatmapCell = (day: number, hour: number) => {
    const pattern = hourlyPatterns.find(p => p.day === day && p.hour === hour);
    const count = pattern?.count || 0;
    const intensity = count / maxCount;
    return { count, intensity };
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.5 }}
      className="surface-elevated rounded-xl p-6"
    >
      <div className="flex items-center gap-2 mb-6">
        <Clock className="h-5 w-5 text-primary" />
        <h3 className="text-lg font-semibold">Time Patterns</h3>
      </div>

      <Tabs defaultValue="heatmap">
        <TabsList className="mb-4">
          <TabsTrigger value="heatmap">Activity Heatmap</TabsTrigger>
          <TabsTrigger value="byDay">By Day</TabsTrigger>
          <TabsTrigger value="byHour">By Hour</TabsTrigger>
        </TabsList>

        <TabsContent value="heatmap">
          <div className="overflow-x-auto">
            <div className="min-w-[600px]">
              {/* Hour labels */}
              <div className="flex mb-1 ml-10">
                {HOURS.filter((_, i) => i % 3 === 0).map((hour, i) => (
                  <div key={hour} className="flex-1 text-xs text-muted-foreground text-center">
                    {hour}
                  </div>
                ))}
              </div>
              
              {/* Heatmap grid */}
              {DAYS.map((day, dayIndex) => (
                <div key={day} className="flex items-center gap-1 mb-1">
                  <div className="w-10 text-xs text-muted-foreground text-right pr-2">
                    {day}
                  </div>
                  <div className="flex-1 flex gap-0.5">
                    {Array.from({ length: 24 }).map((_, hourIndex) => {
                      const { count, intensity } = getHeatmapCell(dayIndex, hourIndex);
                      return (
                        <div
                          key={hourIndex}
                          title={`${day} ${HOURS[hourIndex]}: ${count} scans`}
                          className={cn(
                            "flex-1 h-6 rounded-sm transition-colors cursor-default",
                            intensity === 0 && "bg-muted",
                            intensity > 0 && intensity <= 0.25 && "bg-primary/20",
                            intensity > 0.25 && intensity <= 0.5 && "bg-primary/40",
                            intensity > 0.5 && intensity <= 0.75 && "bg-primary/60",
                            intensity > 0.75 && "bg-primary"
                          )}
                        />
                      );
                    })}
                  </div>
                </div>
              ))}
              
              {/* Legend */}
              <div className="flex items-center justify-end gap-2 mt-4">
                <span className="text-xs text-muted-foreground">Less</span>
                <div className="flex gap-1">
                  <div className="w-4 h-4 rounded-sm bg-muted" />
                  <div className="w-4 h-4 rounded-sm bg-primary/20" />
                  <div className="w-4 h-4 rounded-sm bg-primary/40" />
                  <div className="w-4 h-4 rounded-sm bg-primary/60" />
                  <div className="w-4 h-4 rounded-sm bg-primary" />
                </div>
                <span className="text-xs text-muted-foreground">More</span>
              </div>
            </div>
          </div>
        </TabsContent>

        <TabsContent value="byDay">
          {bestTimes.byDay.every(d => d.count === 0) ? (
            <div className="text-center py-8 text-muted-foreground">
              <BarChart3 className="h-8 w-8 mx-auto mb-2 opacity-50" />
              <p>No data available yet</p>
            </div>
          ) : (
            <div className="h-[200px]">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={bestTimes.byDay}>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
                  <XAxis dataKey="name" stroke="hsl(var(--muted-foreground))" fontSize={12} />
                  <YAxis stroke="hsl(var(--muted-foreground))" fontSize={12} />
                  <Tooltip content={<CustomTooltip />} />
                  <Bar dataKey="count" fill="hsl(var(--primary))" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}
        </TabsContent>

        <TabsContent value="byHour">
          {bestTimes.byHour.every(h => h.count === 0) ? (
            <div className="text-center py-8 text-muted-foreground">
              <BarChart3 className="h-8 w-8 mx-auto mb-2 opacity-50" />
              <p>No data available yet</p>
            </div>
          ) : (
            <div className="h-[200px]">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={bestTimes.byHour}>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
                  <XAxis dataKey="name" stroke="hsl(var(--muted-foreground))" fontSize={10} interval={2} />
                  <YAxis stroke="hsl(var(--muted-foreground))" fontSize={12} />
                  <Tooltip content={<CustomTooltip />} />
                  <Bar dataKey="count" fill="hsl(var(--primary))" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}
        </TabsContent>
      </Tabs>
    </motion.div>
  );
}
